import { useEffect } from "react";

/**
 * Custom hook to lock body scroll when modal is open
 * Prevents background scrolling while modal is visible
 * 
 * @param {boolean} isOpen - Whether the modal is open/visible
 * @param {string[]} allowedSelectors - CSS selectors for elements that should allow scrolling (e.g., modal content)
 */
const useBodyScrollLock = (isOpen, allowedSelectors = ['.ant-modal-content', '.ant-modal-wrap']) => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const body = document.body;
    const html = document.documentElement;

    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      const scrollbarWidth = window.innerWidth - html.clientWidth;

      // Lock scroll - use setProperty with important flag
      body.style.setProperty("overflow", "hidden", "important");
      html.style.setProperty("overflow", "hidden", "important");
      body.style.setProperty("position", "fixed", "important");
      body.style.setProperty("top", `-${scrollY}px`, "important");
      body.style.setProperty("width", "100%", "important");
      
      // Prevent layout shift by adding padding for scrollbar
      if (scrollbarWidth > 0) {
        body.style.setProperty("padding-right", `${scrollbarWidth}px`, "important");
      }

      // Prevent touch scrolling on mobile
      const preventTouchMove = (e) => {
        // Allow scrolling inside modal content
        if (allowedSelectors.some(selector => e.target.closest(selector))) {
          return;
        }
        e.preventDefault();
      };
      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      // Cleanup function to restore scroll
      return () => {
        document.removeEventListener('touchmove', preventTouchMove);
        body.style.removeProperty("overflow");
        body.style.removeProperty("padding-right");
        html.style.removeProperty("overflow");
        body.style.removeProperty("position");
        body.style.removeProperty("top");
        body.style.removeProperty("width");
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, allowedSelectors]);
};

export default useBodyScrollLock;

